import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  MessageCircle,
  ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const LOGO =
  "https://customer-assets-lqy194kg.emergentagent.net/job_nityavastra-shop/artifacts/dirh3az1_Updated%20Logo%20Nityavastra.png";

export default function Login() {
  const {
    user,
    loading: authLoading,
    login,
    loginWithGoogle,
    loginWithFacebook,
    requestWhatsAppLoginOtp,
    verifyWhatsAppLoginOtp,
    logout,
  } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const storedReturnTo = window.sessionStorage.getItem(
    "nityavastra_login_return_to"
  );
  const requestedReturnTo =
    location.state?.returnTo || storedReturnTo || "/";
  const returnTo =
    typeof requestedReturnTo === "string" &&
    requestedReturnTo.startsWith("/") &&
    !requestedReturnTo.startsWith("//")
      ? requestedReturnTo
      : "/";

  const completeCustomerLogin = useCallback(() => {
    window.sessionStorage.removeItem("nityavastra_login_return_to");
    navigate(returnTo, { replace: true });
  }, [navigate, returnTo]);

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState("email");
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const [whatsappStep, setWhatsappStep] = useState("phone");
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [whatsappOtp, setWhatsappOtp] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Customer Platform presentation settings only.
  const [homepageSettings, setHomepageSettings] = useState({});

  const [form, setForm] = useState({
    email: "",
    password: "",
  });

  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(
    Boolean(location.state?.loggedOut)
  );

  useEffect(() => {
    api.get("/homepage/settings")
      .then((r) => setHomepageSettings(r.data || {}))
      .catch(() => setHomepageSettings({}));
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;

    if (user.role === "customer") {
      completeCustomerLogin();
      return;
    }

    const rejectNonCustomerSession = async () => {
      await logout();
      toast.error(
        "This sign-in page is for Nityavastra customers only. Please use your designated portal."
      );
    };

    rejectNonCustomerSession();
  }, [authLoading, user, logout, completeCustomerLogin]);

  useEffect(() => {
    if (!location.state?.loggedOut) return;

    setShowLogoutConfirmation(true);

    const timer = window.setTimeout(() => {
      setShowLogoutConfirmation(false);
      window.history.replaceState({}, document.title, "/login");
    }, 4000);


  return () => window.clearTimeout(timer);
  }, [location.state?.loggedOut, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const loggedInUser = await login(form.email, form.password);

      if (loggedInUser?.role !== "customer") {
        await logout();
        toast.error(
          "This sign-in page is for Nityavastra customers only. Please use your designated portal."
        );
        return;
      }

      toast.success("Welcome back!");
      completeCustomerLogin();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppOtpRequest = async () => {
    setWhatsappLoading(true);
    try {
      await requestWhatsAppLoginOtp(whatsappPhone);
      setWhatsappStep("otp");
      toast.success(
        "If this number is linked, a verification code was sent"
      );
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Unable to start WhatsApp sign-in"
      );
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleWhatsAppOtpVerify = async () => {
    if (!/^\d{6}$/.test(whatsappOtp)) {
      toast.error("Enter the 6-digit verification code");
      return;
    }

    setWhatsappLoading(true);
    try {
      const loggedInUser = await verifyWhatsAppLoginOtp(
        whatsappPhone,
        whatsappOtp
      );
      if (loggedInUser?.role !== "customer") {
        await logout();
        toast.error(
          "This sign-in page is for Nityavastra customers only."
        );
        return;
      }
      toast.success("Welcome back!");
      completeCustomerLogin();
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Unable to verify the WhatsApp code"
      );
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);

    try {
      await loginWithGoogle();
    } catch (e) {
      console.error("Google login failed:", e);
      toast.error(e?.message || "Unable to continue with Google");
      setGoogleLoading(false);
    }
  };

  const handleFacebookLogin = async () => {
    setFacebookLoading(true);

    try {
      await loginWithFacebook();
    } catch (e) {
      console.error("Facebook login failed:", e);
      toast.error(e?.message || "Unable to continue with Facebook");
      setFacebookLoading(false);
    }
  };

  const loginPresentation = {
    login_promo_enabled: true,
    login_title: "Tradition, remembered.",
    login_subtitle:
      "Sign in to continue your journey through authentic sarees, timeless craftsmanship and everyday Indian heritage.",
    login_image: "",
    ...(homepageSettings || {}),
  };

  const absoluteLoginImgUrl = (value) => {
    if (!value) return "";

    if (
      value.startsWith("http://") ||
      value.startsWith("https://") ||
      value.startsWith("data:") ||
      value.startsWith("blob:")
    ) {
      return value;
    }

    // Frontend-owned public assets remain on the customer site.
    if (value.startsWith("/assets/") || value.startsWith("/nityavastra-")) {
      return value;
    }

    const backendOrigin = (API || "").replace(/\/api\/?$/, "");
    return `${backendOrigin}${value.startsWith("/") ? "" : "/"}${value}`;
  };

  const loginSlides = (
    Array.isArray(loginPresentation.login_images) &&
    loginPresentation.login_images.length
      ? loginPresentation.login_images
      : loginPresentation.login_image
        ? [{ image: loginPresentation.login_image, active: true }]
        : []
  )
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `login-slide-${index}`,
          url: item,
          active: true,
        };
      }

      if (item && typeof item === "object") {
        return {
          id: item.id || `login-slide-${index}`,
          url: item.url || item.image || item.src || "",
          active: item.active !== false,
        };
      }

      return null;
    })
    .filter((item) => item && item.url && item.active !== false)
    .slice(0, 4);

  const [loginSlide, setLoginSlide] = useState(0);

  useEffect(() => {
    if (!loginSlides.length) {
      if (loginSlide !== 0) setLoginSlide(0);
      return;
    }

    if (loginSlide >= loginSlides.length) {
      setLoginSlide(0);
    }
  }, [loginSlide, loginSlides.length]);

  useEffect(() => {
    if (
      !loginPresentation.login_auto_rotate ||
      loginSlides.length <= 1
    ) {
      return;
    }

    const seconds = Math.min(
      30,
      Math.max(
        3,
        Number(loginPresentation.login_rotation_seconds) || 5
      )
    );

    const timer = window.setInterval(() => {
      setLoginSlide((current) =>
        (current + 1) % loginSlides.length
      );
    }, seconds * 1000);

    return () => window.clearInterval(timer);
  }, [
    loginSlides.length,
    loginPresentation.login_auto_rotate,
    loginPresentation.login_rotation_seconds,
  ]);

  const goToLoginSlide = (index) => {
    if (!loginSlides.length) return;

    setLoginSlide(
      ((index % loginSlides.length) + loginSlides.length) %
        loginSlides.length
    );
  };

  return (
    <main className="relative overflow-hidden bg-[#F5EBDD] text-[#351A13]">
      {/* Textile texture */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.035]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #5B1725 1px, transparent 0)",
          backgroundSize: "22px 22px",
        }}
        aria-hidden="true"
      />

      <div className="pointer-events-none absolute -left-40 top-20 h-[520px] w-[520px] rounded-full bg-[#B8871E]/10 blur-3xl" />
      <div className="pointer-events-none absolute -right-40 bottom-16 h-[460px] w-[460px] rounded-full bg-[#7C1F30]/10 blur-3xl" />

      <div className="relative z-10 mx-auto flex w-full max-w-[1440px] items-start px-3 pb-4 pt-3 sm:px-4 sm:pb-5 sm:pt-4 lg:min-h-[680px] lg:items-center lg:px-10 lg:pb-[48px] lg:pt-[48px]">
        <div className="grid w-full grid-cols-1 overflow-visible rounded-[24px] border border-[#C9A66B]/55 bg-[#FFFDF8]/95 shadow-[0_18px_55px_rgba(73,43,24,0.12)] lg:grid-cols-[1.08fr_0.92fr] lg:overflow-hidden lg:rounded-[30px] lg:border-[#C9A66B]/75 lg:shadow-[0_28px_80px_rgba(73,43,24,0.18)]">

          {/* Modern daily-wear presentation */}
          {loginPresentation.login_promo_enabled && (
            <section className="relative hidden min-h-[650px] overflow-hidden border-r border-[#E7D7C5] lg:block">

              {/* Full-bleed Admin-controlled carousel */}
              <div className="absolute inset-0">
                {loginSlides.length > 0 ? (
                  loginSlides.map((slide, index) => {
                    const active = index === loginSlide;

                    const motion =
                      loginPresentation.login_transition === "slide"
                        ? active
                          ? "translate-x-0 opacity-100"
                          : index < loginSlide
                            ? "-translate-x-full opacity-0"
                            : "translate-x-full opacity-0"
                        : active
                          ? "opacity-100 scale-100"
                          : "opacity-0 scale-[1.025]";

                    return (
                      <img
                        key={slide.id || index}
                        src={absoluteLoginImgUrl(slide.url)}
                        alt={`Nityavastra cotton saree style ${index + 1}`}
                        className={`absolute inset-0 h-full w-full object-cover object-center transition-all duration-700 ease-out ${motion}`}
                        loading={index === 0 ? "eager" : "lazy"}
                      />
                    );
                  })
                ) : (
                  <div className="absolute inset-0 bg-[linear-gradient(135deg,#FFB703_0%,#FF8FA3_35%,#E2006A_70%,#007C83_100%)]" />
                )}
              </div>

              {/* Overlay keeps text readable without hiding the saree */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent" />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-black/15" />

              {/* Brand */}
              <div className="absolute left-10 top-10 z-20 flex items-center gap-4 xl:left-14 xl:top-12">
                <img
                  src="/nityavastra-logo.png"
                  alt="Nityavastra"
                  className="h-[72px] w-[72px] rounded-full bg-white/90 object-contain p-1 shadow-lg"
                />

                <div>
                  <p className="font-serif text-[22px] font-semibold tracking-[0.08em] text-white drop-shadow">
                    NITYAVASTRA
                  </p>

                  <p className="mt-1 text-[9px] font-semibold uppercase tracking-[0.28em] text-[#FFE39B]">
                    Heritage of India
                  </p>
                </div>
              </div>

              {/* Campaign text */}
              <div className="absolute left-10 top-[27%] z-20 max-w-[450px] xl:left-14">
                <div className="mb-4 flex items-center gap-3">
                  <span className="h-px w-10 bg-[#FFCF45]" />

                  <p className="text-[10px] font-bold uppercase tracking-[0.34em] text-[#FFE39B]">
                    Everyday Cotton Edit
                  </p>
                </div>

                <h1 className="font-serif text-[3.2rem] font-medium leading-[0.94] tracking-[-0.035em] text-white drop-shadow-lg xl:text-[4rem]">
                  {loginPresentation.login_title ||
                    "Wear your everyday story."}
                </h1>

                <p className="mt-5 max-w-[430px] text-[15px] leading-7 text-white/90 drop-shadow">
                  {loginPresentation.login_subtitle ||
                    "Fresh cotton sarees, colourful prints and effortless drapes made for everyday life."}
                </p>
              </div>

              {/* Carousel information + controls */}
              <div className="absolute bottom-6 left-10 right-10 z-20 xl:left-14 xl:right-14">
                <div className="rounded-2xl border border-white/30 bg-black/30 px-5 py-4 shadow-xl backdrop-blur-md">

                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <p className="text-[9px] font-bold uppercase tracking-[0.28em] text-[#FFD15C]">
                        Modern · Ethnic · Everyday
                      </p>

                      <p className="mt-1 font-serif text-xl text-white">
                        Colourful cotton. Effortless expression.
                      </p>
                    </div>

                    {loginSlides.length > 1 && (
                      <div className="flex shrink-0 gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            goToLoginSlide(loginSlide - 1)
                          }
                          aria-label="Previous login image"
                          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/50 bg-white/90 text-xl text-[#7C1F30] transition hover:bg-white"
                        >
                          ‹
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            goToLoginSlide(loginSlide + 1)
                          }
                          aria-label="Next login image"
                          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/50 bg-white/90 text-xl text-[#7C1F30] transition hover:bg-white"
                        >
                          ›
                        </button>
                      </div>
                    )}
                  </div>

                  {loginSlides.length > 1 && (
                    <div className="mt-4 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {loginSlides.map((_, index) => (
                          <button
                            key={index}
                            type="button"
                            onClick={() =>
                              goToLoginSlide(index)
                            }
                            aria-label={`Show login image ${index + 1}`}
                            className={`h-2.5 rounded-full transition-all duration-300 ${
                              index === loginSlide
                                ? "w-8 bg-[#FFCF45]"
                                : "w-2.5 bg-white/45 hover:bg-white/80"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Customer login */}
          <section className="relative flex min-h-0 items-start justify-center bg-[#FFFDF8]/95 px-4 py-4 sm:px-7 sm:py-5 lg:min-h-[650px] lg:items-start lg:px-12 lg:pb-8 lg:pt-12">
            <div className="relative z-10 w-full max-w-[430px]">
              {/* Mobile interactive campaign carousel */}
              <div className="mb-3 lg:hidden">
                <div className="relative left-1/2 w-[calc(100vw-24px)] max-w-[744px] -translate-x-1/2 overflow-hidden rounded-[20px] sm:w-[calc(100vw-32px)]">
                  <div className="relative h-[330px] overflow-hidden bg-[linear-gradient(135deg,#FFB703,#E2006A,#008C8C)] sm:h-[380px] md:h-[410px]">
                    {loginSlides.length > 0 ? (
                      loginSlides.map((slide, index) => {
                        const active = index === loginSlide;

                        const motion =
                          loginPresentation.login_transition === "slide"
                            ? active
                              ? "translate-x-0 opacity-100"
                              : index < loginSlide
                                ? "-translate-x-full opacity-0"
                                : "translate-x-full opacity-0"
                            : active
                              ? "opacity-100 scale-100"
                              : "opacity-0 scale-[1.03]";

                        return (
                          <img
                            key={slide.id || index}
                            src={absoluteLoginImgUrl(slide.url)}
                            alt={`Nityavastra cotton saree style ${index + 1}`}
                            className={`absolute inset-0 h-full w-full object-cover object-center transition-all duration-700 ease-out ${motion}`}
                            loading={index === 0 ? "eager" : "lazy"}
                          />
                        );
                      })
                    ) : (
                      <div className="absolute inset-0 bg-[linear-gradient(135deg,#FFB703_0%,#FF6B9E_38%,#E2006A_68%,#007C83_100%)]" />
                    )}

                    {/* Image readability overlays */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-black/45 via-black/10 to-transparent" />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-black/10" />

                    {/* Mobile brand */}
                    <div className="absolute left-4 top-4 z-20 flex items-center gap-2.5">
                      <img
                        src="/nityavastra-logo.png"
                        alt="Nityavastra"
                        className="h-11 w-11 rounded-full bg-white/95 object-contain p-0.5 shadow-md"
                      />

                      <div>
                        <p className="font-serif text-[14px] font-semibold tracking-[0.08em] text-white">
                          NITYAVASTRA
                        </p>
                        <p className="text-[7px] font-semibold uppercase tracking-[0.22em] text-[#FFE59A]">
                          Heritage of India
                        </p>
                      </div>
                    </div>

                    {/* Mobile campaign copy */}
                    <div className="absolute bottom-[78px] left-5 z-20 max-w-[235px]">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="h-px w-6 bg-[#FFD15C]" />
                        <span className="text-[8px] font-bold uppercase tracking-[0.25em] text-[#FFE59A]">
                          Everyday Cotton Edit
                        </span>
                      </div>

                      <h1 className="font-serif text-[2rem] font-medium leading-[0.95] tracking-[-0.025em] text-white drop-shadow-md">
                        {loginPresentation.login_title || "Wear your everyday story."}
                      </h1>

                      <p className="mt-2 line-clamp-2 text-[11px] leading-[17px] text-white/90">
                        {loginPresentation.login_subtitle ||
                          "Fresh cotton sarees, colourful prints and effortless drapes made for everyday life."}
                      </p>
                    </div>

                    {/* Tag + dots + arrows */}
                    <div className="absolute inset-x-4 bottom-4 z-20">
                      <div className="flex items-end justify-between gap-3">
                        <div>
                          <span className="inline-flex rounded-full border border-[#FFD15C]/70 bg-black/25 px-3 py-1.5 text-[8px] font-semibold uppercase tracking-[0.16em] text-[#FFE59A] backdrop-blur-sm">
                            Modern · Ethnic · Everyday
                          </span>

                          {loginSlides.length > 1 && (
                            <div className="mt-3 flex items-center gap-2">
                              {loginSlides.map((_, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => goToLoginSlide(index)}
                                  aria-label={`Show login image ${index + 1}`}
                                  className={`h-2.5 rounded-full transition-all duration-300 ${
                                    index === loginSlide
                                      ? "w-7 bg-[#FFD15C]"
                                      : "w-2.5 bg-white/60"
                                  }`}
                                />
                              ))}
                            </div>
                          )}
                        </div>

                        {loginSlides.length > 1 && (
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => goToLoginSlide(loginSlide - 1)}
                              aria-label="Previous login image"
                              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/60 bg-white/95 text-2xl text-[#8B1E32] shadow-lg"
                            >
                              ‹
                            </button>

                            <button
                              type="button"
                              onClick={() => goToLoginSlide(loginSlide + 1)}
                              aria-label="Next login image"
                              className="flex h-11 w-11 items-center justify-center rounded-full border border-white/60 bg-white/95 text-2xl text-[#8B1E32] shadow-lg"
                            >
                              ›
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {showLogoutConfirmation && (
                <div
                  className="mb-5 animate-[fadeSlideDown_450ms_ease-out]"
                  role="status"
                  aria-live="polite"
                >
                  <div className="flex items-center gap-3 rounded-2xl border border-[#D9C39B] bg-[#FFF9EF] px-4 py-3 shadow-sm">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#7C1F30]/10">
                      <CheckCircle2 className="h-5 w-5 text-[#7C1F30]" />
                    </div>
                    <div>
                      <p className="font-serif text-[15px] text-[#351A13]">
                        Signed out successfully
                      </p>
                      <p className="text-[11px] text-[#78716C]">
                        Your account has been securely signed out.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-4">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.32em] text-[#B8871E]">
                  Customer Sign In
                </p>
                <h2 className="font-serif text-[2.15rem] font-medium leading-tight tracking-[-0.025em] text-[#351A13]">
                  Welcome back
                </h2>
                <p className="mt-2 text-[13px] leading-5 text-[#766154]">
                  Access your Nityavastra account, wishlist and orders.
                </p>
              </div>

              <div
                className="mb-5 flex items-center justify-center gap-3"
                role="group"
                aria-label="Choose sign-in method"
              >
                {[
                  { id: "email", label: "Email", icon: <Mail className="h-5 w-5" /> },
                  {
                    id: "google",
                    label: "Google",
                    icon: (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                        <path fill="#4285F4" d="M21.35 12.23c0-.79-.07-1.55-.23-2.27H12v4.3h5.22a4.46 4.46 0 0 1-1.94 2.93v2.39h3.14c1.84-1.69 2.93-4.18 2.93-7.35Z" />
                        <path fill="#34A853" d="M12 21.65c2.63 0 4.84-.87 6.45-2.37l-3.14-2.39c-.87.58-1.98.93-3.31.93-2.55 0-4.71-1.72-5.49-4.04H3.27v2.46A9.74 9.74 0 0 0 12 21.65Z" />
                        <path fill="#FBBC05" d="M6.51 13.78A5.86 5.86 0 0 1 6.2 12c0-.62.11-1.22.31-1.78V7.76H3.27A9.73 9.73 0 0 0 2.25 12c0 1.57.38 3.05 1.02 4.24l3.24-2.46Z" />
                        <path fill="#EA4335" d="M12 6.18c1.43 0 2.72.49 3.74 1.46l2.8-2.8C16.83 3.2 14.63 2.35 12 2.35a9.74 9.74 0 0 0-8.73 5.41l3.24 2.46C7.29 7.9 9.45 6.18 12 6.18Z" />
                      </svg>
                    ),
                  },
                  {
                    id: "facebook",
                    label: "Facebook",
                    icon: (
                      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                        <path fill="#1877F2" d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.02 10.13 11.93v-8.43H7.08v-3.5h3.05V9.41c0-3.04 1.79-4.73 4.56-4.73 1.32 0 2.7.24 2.7.24v2.99h-1.52c-1.5 0-1.97.94-1.97 1.9v2.26h3.35l-.54 3.5H13.9V24C19.61 23.09 24 18.09 24 12.07Z" />
                      </svg>
                    ),
                  },
                  {
                    id: "whatsapp",
                    label: "WhatsApp",
                    icon: (
                      <svg viewBox="0 0 32 32" className="h-6 w-6" aria-hidden="true">
                        <path
                          fill="#25D366"
                          d="M16 .8A15.1 15.1 0 0 0 3 23.6L.8 31.2l7.8-2A15.2 15.2 0 1 0 16 .8Zm0 27.6a12.3 12.3 0 0 1-6.3-1.7l-.5-.3-4.6 1.2 1.2-4.5-.3-.5A12.4 12.4 0 1 1 16 28.4Zm6.8-9.3c-.4-.2-2.2-1.1-2.5-1.2-.3-.1-.6-.2-.8.2-.2.4-.9 1.2-1.1 1.4-.2.2-.4.3-.8.1-.4-.2-1.6-.6-3-1.9-1.1-1-1.9-2.2-2.1-2.6-.2-.4 0-.6.2-.8l.6-.7c.2-.2.3-.4.4-.7.1-.3 0-.5 0-.7l-1.2-2.9c-.3-.7-.6-.6-.8-.6h-.7c-.3 0-.7.1-1 .4-.3.4-1.3 1.3-1.3 3.1s1.3 3.6 1.5 3.8c.2.2 2.6 4 6.4 5.5.9.4 1.6.6 2.2.7.9.3 1.7.2 2.3.1.7-.1 2.2-.9 2.5-1.8.3-.9.3-1.7.2-1.8-.1-.2-.3-.3-.7-.5Z"
                        />
                      </svg>
                    ),
                  },
                ].map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => {
                      setLoginMethod(method.id);
                      if (method.id === "whatsapp") {
                        setWhatsappStep("phone");
                        setWhatsappOtp("");
                      }
                    }}
                    disabled={loading || googleLoading || facebookLoading || whatsappLoading}
                    aria-label={`Sign in with ${method.label}`}
                    aria-pressed={loginMethod === method.id}
                    title={method.label}
                    className={`flex h-12 w-12 items-center justify-center rounded-full border bg-white transition ${
                      loginMethod === method.id
                        ? "border-[#7C1F30] ring-2 ring-[#7C1F30]/20"
                        : "border-[#D8C4A8] hover:border-[#7C1F30]"
                    }`}
                  >
                    {method.icon}
                  </button>
                ))}
              </div>

              <form onSubmit={submit} className="space-y-3">
                {loginMethod === "email" && (
                  <>
                <div>
                  <Label
                    htmlFor="login-email"
                    className="mb-1.5 block text-[13px] font-semibold text-[#351A13]"
                  >
                    Email address
                  </Label>

                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A18C7B]" />
                    <Input
                      id="login-email"
                      type="email"
                      required
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={form.email}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          email: e.target.value,
                        })
                      }
                      data-testid="login-email"
                      className="h-12 rounded-xl border-[#D8C4A8] bg-white pl-11 text-sm text-[#351A13] shadow-sm placeholder:text-[#AA9687] focus:border-[#7C1F30] focus:ring-[#7C1F30]/20"
                    />
                  </div>
                </div>

                <div>
                  <div className="mb-1.5 flex items-center justify-between gap-4">
                    <Label
                      htmlFor="login-password"
                      className="text-[13px] font-semibold text-[#351A13]"
                    >
                      Password
                    </Label>

                    <Link
                      to="/forgot-password"
                      className="text-xs font-semibold text-[#7C1F30] transition-colors hover:text-[#9A3146] hover:underline"
                    >
                      Forgot Password?
                    </Link>
                  </div>

                  <div className="relative">
                    <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A18C7B]" />
                    <Input
                      id="login-password"
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      value={form.password}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          password: e.target.value,
                        })
                      }
                      data-testid="login-password"
                      className="h-12 rounded-xl border-[#D8C4A8] bg-white pl-11 pr-12 text-sm text-[#351A13] shadow-sm placeholder:text-[#AA9687] focus:border-[#7C1F30] focus:ring-[#7C1F30]/20"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute right-2 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-[#78716C] transition-colors hover:bg-[#FAF3E7] hover:text-[#7C1F30]"
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading || googleLoading || facebookLoading}
                  data-testid="login-submit"
                  className="h-12 w-full rounded-xl bg-[#7C1F30] px-6 font-semibold text-white shadow-[0_12px_26px_rgba(124,31,48,0.20)] transition-all hover:-translate-y-0.5 hover:bg-[#8D2A3D] hover:shadow-[0_15px_30px_rgba(124,31,48,0.26)]"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </Button>

                <div className="flex items-center justify-center gap-2 text-[11px] text-[#78716C]">
                  <ShieldCheck className="h-4 w-4 text-[#B8871E]" />
                  <span>Secure customer account access</span>
                </div>

                  </>
                )}

                {loginMethod === "google" && (
                  <Button
                    type="button"
                    onClick={handleGoogleLogin}
                    disabled={loading || googleLoading || facebookLoading}
                    data-testid="login-google"
                    className="h-11 w-full rounded-xl bg-[#7C1F30] text-white hover:bg-[#8D2A3D]"
                  >
                    {googleLoading ? "Connecting..." : "Continue with Google"}
                  </Button>
                )}

                {loginMethod === "facebook" && (
                  <Button
                    type="button"
                    onClick={handleFacebookLogin}
                    disabled={loading || googleLoading || facebookLoading}
                    data-testid="login-facebook"
                    className="h-11 w-full rounded-xl bg-[#1877F2] text-white hover:bg-[#1669D6]"
                  >
                    {facebookLoading ? "Connecting..." : "Continue with Facebook"}
                  </Button>
                )}

                {loginMethod === "whatsapp" && (
                  <div className="space-y-3 rounded-xl border border-[#B7DDD5] bg-[#F2FBF8] p-4">
                    <p className="text-xs leading-5 text-[#49675F]">
                      For customers who have linked and verified their
                      WhatsApp number in their Nityavastra profile.
                    </p>

                    <label className="relative block cursor-text">
                      <span className="sr-only">10-digit WhatsApp mobile number</span>
                      <span className="flex justify-between gap-1" aria-hidden="true">
                        {Array.from({ length: 10 }, (_, index) => (
                          <span
                            key={index}
                            className={`flex h-11 min-w-0 flex-1 items-center justify-center rounded-xl border bg-white text-base font-semibold text-[#351A13] ${
                              !whatsappLoading &&
                              whatsappStep === "phone" &&
                              index === whatsappPhone.length
                                ? "border-[#128C7E] ring-1 ring-[#128C7E]/30"
                                : "border-[#9CCFC4]"
                            }`}
                          >
                            {whatsappPhone[index] || ""}
                          </span>
                        ))}
                      </span>
                      <input
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        maxLength={10}
                        value={whatsappPhone}
                        onChange={(e) =>
                          setWhatsappPhone(
                            e.target.value.replace(/\D/g, "").slice(0, 10)
                          )
                        }
                        disabled={whatsappLoading || whatsappStep === "otp"}
                        data-testid="login-whatsapp-phone"
                        aria-label="10-digit WhatsApp mobile number"
                        className="absolute inset-0 z-10 h-full w-full cursor-text opacity-0 disabled:cursor-not-allowed"
                      />
                    </label>

                    {whatsappStep === "phone" ? (
                      <Button
                        type="button"
                        onClick={handleWhatsAppOtpRequest}
                        disabled={
                          whatsappLoading || whatsappPhone.length !== 10
                        }
                        className="h-11 w-full rounded-xl bg-[#128C7E] text-white hover:bg-[#0F766E]"
                        data-testid="login-whatsapp-send"
                      >
                        {whatsappLoading
                          ? "Sending..."
                          : "Send verification code"}
                      </Button>
                    ) : (
                      <>
                        <Input
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          placeholder="6-digit verification code"
                          value={whatsappOtp}
                          onChange={(e) =>
                            setWhatsappOtp(
                              e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 6)
                            )
                          }
                          disabled={whatsappLoading}
                          data-testid="login-whatsapp-otp"
                          className="h-11 rounded-xl border-[#9CCFC4] bg-white text-sm"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            disabled={whatsappLoading}
                            onClick={() => {
                              setWhatsappStep("phone");
                              setWhatsappOtp("");
                            }}
                            className="h-10 rounded-xl border-[#9CCFC4] bg-white text-[#0F766E]"
                          >
                            Change number
                          </Button>
                          <Button
                            type="button"
                            onClick={handleWhatsAppOtpVerify}
                            disabled={whatsappLoading}
                            className="h-10 rounded-xl bg-[#128C7E] text-white hover:bg-[#0F766E]"
                            data-testid="login-whatsapp-verify"
                          >
                            {whatsappLoading
                              ? "Verifying..."
                              : "Verify & sign in"}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <p className="pt-2 text-center text-sm text-[#6B5A4D]">
                  New to Nityavastra?{" "}
                  <Link
                    to="/register"
                    className="font-semibold text-[#7C1F30] hover:text-[#8D2A3D] hover:underline"
                    data-testid="link-register"
                  >
                    Create an account
                  </Link>
                </p>
              </form>

              <div className="mt-7 border-t border-[#E8DDCF] pt-5">
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 text-[10px] uppercase tracking-[0.12em] text-[#8A7566]">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-[#B8871E]" />
                    Secure Payments
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#B8871E]" />
                    Authentic Quality
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#B8871E]" />
                    Easy Returns
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#B8871E]" />
                    Customer Support
                  </div>
                </div>
              </div>

              <p className="mt-6 text-center text-[9px] uppercase tracking-[0.18em] text-[#A08E81]">
                Your privacy and security matter to us
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
